import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { Subject, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { UserService } from '../../../../@shared/services/user.service';
import { AuthUserService } from '../../../../@shared/services/auth/auth-user.service';
import { SeasonsService } from './seasons.service';
import { ISeason, ISeasonalQuest } from './seasons.interfaces';
import {
  SeasonalQuestTypeEnum,
  SeasonalQuestDisplayTypeEnum,
  SeasonalQuestInstanceStatusEnum,
  SeasonStatusEnum,
} from './seasons.enums';
import { SeasonScoreDialogComponent } from './season-score-dialog.component';
import { SeasonRewardsDialogComponent } from './season-rewards-dialog.component';

type Tab = 'seasons' | 'quests';
interface ListState<T> {
  rows: T[];
  amount: number;
  pageIndex: number;
  pageSize: number;
  sort: Sort;
  loading: boolean;
  error: string;
}
function listState<T>(): ListState<T> {
  return {
    rows: [],
    amount: 0,
    pageIndex: 0,
    pageSize: 20,
    sort: { active: '', direction: '' },
    loading: false,
    error: '',
  };
}

@Component({
  selector: 'app-user-seasons',
  templateUrl: './seasons.component.html',
  styleUrls: ['./seasons.component.scss'],
})
export class SeasonsComponent implements OnInit, OnDestroy {
  private readonly destroyed$ = new Subject<void>();
  private readonly refresh = {
    seasons: new Subject<void>(),
    quests: new Subject<void>(),
  };
  private activeDialog?: MatDialogRef<any>;
  userId: number;
  canEdit = false;
  seasons = listState<ISeason>();
  quests = listState<ISeasonalQuest>();
  readonly seasonStatuses = Object.values(SeasonStatusEnum);
  readonly questStatuses = Object.values(SeasonalQuestInstanceStatusEnum);
  readonly questTypes = Object.values(SeasonalQuestTypeEnum);
  readonly displayTypes = Object.values(SeasonalQuestDisplayTypeEnum);
  readonly seasonDates = [
    { key: 'minStartAt', label: 'Начало от' },
    { key: 'maxStartAt', label: 'Начало до' },
    { key: 'minEndAt', label: 'Конец от' },
    { key: 'maxEndAt', label: 'Конец до' },
    { key: 'minRaffleEndAt', label: 'Конец раффлов от' },
    { key: 'maxRaffleEndAt', label: 'Конец раффлов до' },
  ];
  readonly questDates = [
    { key: 'minCompletedAt', label: 'Выполнен от' },
    { key: 'maxCompletedAt', label: 'Выполнен до' },
    { key: 'minClaimedAt', label: 'Забран от' },
    { key: 'maxClaimedAt', label: 'Забран до' },
  ];
  readonly seasonColumns = [
    'id',
    'name',
    'startAt',
    'endAt',
    'raffleEndAt',
    'status',
    'actions',
  ];
  readonly questColumns = [
    'id',
    'questDefinitionId',
    'seasonId',
    'name',
    'type',
    'group',
    'displayType',
    'scoreAmount',
    'status',
    'progress',
    'completedAt',
    'claimedAt',
  ];
  readonly seasonForm: FormGroup = this.fb.group({
    ids: '',
    statuses: [['active']],
    minStartAt: '',
    maxStartAt: '',
    minEndAt: '',
    maxEndAt: '',
    minRaffleEndAt: '',
    maxRaffleEndAt: '',
  });
  readonly questForm: FormGroup = this.fb.group({
    seasonIds: '',
    types: [[]],
    groups: '',
    minScoreAmount: null,
    maxScoreAmount: null,
    statuses: [[]],
    displayTypes: [[]],
    minCompletedAt: '',
    maxCompletedAt: '',
    minClaimedAt: '',
    maxClaimedAt: '',
  });

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: SeasonsService,
    private readonly users: UserService,
    private readonly auth: AuthUserService,
    private readonly dialog: MatDialog,
  ) {}
  ngOnInit(): void {
    this.auth.user$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(
        user =>
          (this.canEdit =
            user?.roles?.some((role: string) =>
              ['main_admin', 'main_owner'].includes(role),
            ) ?? false),
      );
    // Независимые потоки отменяют устаревший запрос при смене фильтров или пользователя.
    for (const tab of ['seasons', 'quests'] as Tab[]) {
      const state = this[tab];
      this.refresh[tab]
        .pipe(
          switchMap(() => {
            state.loading = true;
            state.error = '';
            let params: object;
            try {
              params = this.params(tab);
            } catch (error) {
              state.error =
                error instanceof Error
                  ? error.message
                  : 'Некорректные фильтры.';
              state.loading = false;
              return of(null);
            }
            const request: Observable<{
              rows: (ISeason | ISeasonalQuest)[];
              amount: number;
            }> =
              tab === 'seasons'
                ? this.api
                    .getSeasons(this.userId, params)
                    .pipe(
                      map(page => ({
                        rows: page.seasons,
                        amount: page.metadata.amount,
                      })),
                    )
                : this.api
                    .getQuests(this.userId, params)
                    .pipe(
                      map(page => ({
                        rows: page.quests,
                        amount: page.metadata.amount,
                      })),
                    );
            return request.pipe(
              catchError(() => {
                state.error = 'Не удалось загрузить данные. Повторите запрос.';
                return of(null);
              }),
              finalize(() => (state.loading = false)),
            );
          }),
          takeUntil(this.destroyed$),
        )
        .subscribe(page => {
          if (tab === 'seasons')
            this.seasons.rows = (page?.rows ?? []) as ISeason[];
          else this.quests.rows = (page?.rows ?? []) as ISeasonalQuest[];
          state.amount = page?.amount ?? 0;
          if (
            page &&
            !page.rows.length &&
            state.amount &&
            state.pageIndex > 0
          ) {
            state.pageIndex = Math.ceil(state.amount / state.pageSize) - 1;
            this.reload(tab);
          }
        });
    }
    this.users.currentUserId
      .pipe(
        filter(id => Number(id) > 0),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(id => {
        this.activeDialog?.close();
        this.userId = Number(id);
        this.seasons.rows = [];
        this.quests.rows = [];
        this.seasons.pageIndex = 0;
        this.quests.pageIndex = 0;
        this.reload('seasons');
        this.reload('quests');
      });
  }
  reload(tab: Tab): void {
    if (this.userId) this.refresh[tab].next();
  }
  apply(tab: Tab): void {
    this[tab].pageIndex = 0;
    this.reload(tab);
  }
  reset(tab: Tab): void {
    const form = tab === 'seasons' ? this.seasonForm : this.questForm;
    form.reset(
      tab === 'seasons'
        ? { statuses: ['active'] }
        : { types: [], statuses: [], displayTypes: [] },
    );
    this[tab].sort = { active: '', direction: '' };
    this.apply(tab);
  }
  page(tab: Tab, event: PageEvent): void {
    this[tab].pageIndex = event.pageIndex;
    this[tab].pageSize = event.pageSize;
    this.reload(tab);
  }
  sort(tab: Tab, value: Sort): void {
    this[tab].sort = value;
    this.apply(tab);
  }
  open(season: ISeason, mode: 'score' | 'prizes' | 'score-rewards'): void {
    const data = { userId: this.userId, season, mode, canEdit: this.canEdit };
    this.activeDialog =
      mode === 'score'
        ? this.dialog.open(SeasonScoreDialogComponent, { width: '680px', data })
        : this.dialog.open(SeasonRewardsDialogComponent, {
            width: '980px',
            data,
          });
    this.activeDialog
      .afterClosed()
      .pipe(takeUntil(this.destroyed$))
      .subscribe(changed => {
        if (changed) this.reload('seasons');
      });
  }
  /** Префиксная сортировка HTTP и UTC-границы соответствуют остальным разделам админки. */
  private params(tab: Tab): object {
    const state = this[tab];
    const value = (tab === 'seasons' ? this.seasonForm : this.questForm).value;
    const params: Record<string, unknown> = {
      pagination: {
        limit: state.pageSize,
        offset: state.pageIndex * state.pageSize,
      },
    };
    const idKey = tab === 'seasons' ? 'ids' : 'seasonIds';
    if (value[idKey]?.trim()) {
      const ids = value[idKey]
        .split(/[\s,;]+/)
        .filter(Boolean)
        .map(Number);
      if (ids.some((id: number) => !Number.isSafeInteger(id) || id < 1))
        throw new Error('ID должны быть положительными целыми числами.');
      params[idKey] = ids;
    }
    for (const key of ['statuses', 'types', 'displayTypes'])
      if (value[key]?.length) params[key] = value[key];
    if (value.groups?.trim())
      params.groups = value.groups
        .split(',')
        .map((group: string) => group.trim())
        .filter(Boolean);
    for (const key of ['minScoreAmount', 'maxScoreAmount'])
      if (
        value[key] !== null &&
        value[key] !== undefined &&
        value[key] !== ''
      ) {
        const amount = Number(value[key]);
        if (
          !Number.isFinite(amount) ||
          amount < 0 ||
          amount > Number.MAX_SAFE_INTEGER
        )
          throw new Error('Очки должны быть неотрицательным числом.');
        params[key] = amount;
      }
    for (const field of tab === 'seasons' ? this.seasonDates : this.questDates)
      if (value[field.key])
        params[field.key] = new Date(value[field.key] + 'Z').toISOString();
    for (const suffix of [
      'StartAt',
      'EndAt',
      'RaffleEndAt',
      'CompletedAt',
      'ClaimedAt',
      'ScoreAmount',
    ]) {
      const min = params['min' + suffix],
        max = params['max' + suffix];
      if (min !== undefined && max !== undefined && min > max)
        throw new Error('Граница «от» не может превышать границу «до».');
    }
    if (state.sort.direction)
      params.sortBy =
        (state.sort.direction === 'desc' ? '-' : '') + state.sort.active;
    return params;
  }
  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
    this.activeDialog?.close();
  }
}
